import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import './LoginPage.scss';

function safeRedirect(raw: string | null): string {
  if (!raw) return '/dashboard';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/dashboard';
  return raw;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = safeRedirect(searchParams.get('redirect'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const normalizedEmail = (email || '').trim().toLowerCase();
      const response = await authAPI.login(normalizedEmail, password);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate(redirectTo);
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.errors.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__container">
        <button type="button" className="page-back" onClick={() => navigate('/')} aria-label={t('nav.back')}>
          ← {t('nav.back')}
        </button>
        <header className="login-page__header">
          <h1 className="login-page__title">{t('login.title')}</h1>
        </header>
        <form className="login-page__form" onSubmit={handleSubmit}>
          {error && <div className="form-error-message">{error}</div>}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              {t('login.form.email')}
            </label>
            <input
              id="email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              {t('login.form.password')}
            </label>
            <input
              id="password"
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? t('login.form.loading') : t('login.form.submit')}
          </button>
        </form>
        <p className="login-page__footer">
          <Link to="/forgot-password" className="link">
            {t('login.forgotPassword')}
          </Link>
        </p>
        <p className="login-page__footer">
          {t('login.noAccount')}{' '}
          <Link to={`/register?redirect=${encodeURIComponent(redirectTo)}`} className="link">
            {t('login.register')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
